@if (isForbidden) {
  <div class="modal-body">
    <app-not-authorized
      title="You cannot adjust stock"
      message="Manual stock adjustments are restricted to administrators."></app-not-authorized>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="onCancel()">Close</button>
  </div>
} @else {
  <form [formGroup]="adjustmentForm" (ngSubmit)="onSubmit()">
    <div class="modal-body p-4">
      <!-- What is being adjusted. Fixed: the form is always opened from a known batch. -->
      <div class="adjustment-target mb-4">
        <div class="row g-3">
          <div class="col-md-6">
            <span class="form-label text-muted small mb-1 d-block">Batch</span>
            <div class="fw-semibold font-monospace">{{ context.batch_number }}</div>
            <div class="text-muted small">{{ context.product_name }} &middot; {{ context.packaging_label }}</div>
          </div>
          <div class="col-md-6">
            <span class="form-label text-muted small mb-1 d-block">{{ containerLabel }}</span>
            @if (needsWarehousePicker) {
              <!-- One batch can sit in several warehouses with different balances, so this -->
              <!-- is asked for rather than inferred from the batch. -->
              <ng-select
                placeholder="Select warehouse"
                [items]="context.warehouseOptions || []"
                formControlName="warehouse"
                bindValue="id"
                bindLabel="name"
                [clearable]="false"
                [class.is-invalid]="(warehouseControl.invalid && warehouseControl.touched) || !!warehouseError" />
              @if (warehouseControl.touched && warehouseControl.invalid) {
                <div class="text-danger small mt-1">Pick the warehouse this adjustment applies to.</div>
              } @else if (warehouseError) {
                <div class="text-danger small mt-1">{{ warehouseError }}</div>
              }
            } @else {
              <div class="fw-semibold">{{ containerName }}</div>
            }
            @if (batchBalanceBefore !== null) {
              <div class="text-muted small">
                In this batch now:
                <span class="fw-semibold">{{ formatQuantity(context.batch_units_available) }} units</span>
              </div>
            }
          </div>
        </div>
      </div>

      @if (productionEntryError) {
        <div class="alert alert-danger py-2 px-3 small">{{ productionEntryError }}</div>
      }

      <!-- Direction. Deliberately the largest control on the form: adding when a -->
      <!-- removal was meant doubles the error rather than correcting it. -->
      <div class="mb-4">
        <label class="form-label">What is happening to the stock?</label>
        <div class="direction-toggle" role="group" aria-label="Adjustment direction">
          <button
            type="button"
            class="direction-option direction-add"
            [class.active]="!isRemoving"
            [attr.aria-pressed]="!isRemoving"
            (click)="setDirection(Direction.ADD)">
            <i class="bi bi-plus-circle-fill"></i>
            <span class="direction-title">Add stock</span>
            <span class="direction-hint">Goods on the shelf that the system is missing</span>
          </button>
          <button
            type="button"
            class="direction-option direction-remove"
            [class.active]="isRemoving"
            [attr.aria-pressed]="isRemoving"
            (click)="setDirection(Direction.REMOVE)">
            <i class="bi bi-dash-circle-fill"></i>
            <span class="direction-title">Remove stock</span>
            <span class="direction-hint">Goods the system counts that are not there</span>
          </button>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-md-6">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <label for="adjustmentQuantity" class="form-label mb-0">
              Quantity
              <span class="text-danger">*</span>
            </label>
            @if (canUseBoxes) {
              <div class="btn-group btn-group-sm" role="group" aria-label="Count in boxes or units">
                <button
                  type="button"
                  class="btn"
                  [class.btn-primary]="isBoxMode"
                  [class.btn-outline-secondary]="!isBoxMode"
                  (click)="setMode(Mode.BOXES)">
                  Boxes
                </button>
                <button
                  type="button"
                  class="btn"
                  [class.btn-primary]="!isBoxMode"
                  [class.btn-outline-secondary]="isBoxMode"
                  (click)="setMode(Mode.UNITS)">
                  Units
                </button>
              </div>
            }
          </div>

          <div class="input-group">
            <span class="input-group-text" [class.text-danger]="isRemoving" [class.text-success]="!isRemoving">
              {{ isRemoving ? '&minus;' : '+' }}
            </span>
            <input
              id="adjustmentQuantity"
              type="number"
              class="form-control shadow-none"
              formControlName="quantity"
              placeholder="0"
              step="0.01"
              min="0"
              [class.is-invalid]="(quantityControl.invalid && quantityControl.touched) || !!quantityError"
              (input)="quantityError = null; shortageError = null" />
            <span class="input-group-text bg-light text-muted">{{ isBoxMode ? 'boxes' : 'units' }}</span>
          </div>

          <!-- The multiplication the admin must never be asked to do by hand. -->
          @if (conversionLabel) {
            <div class="text-muted small mt-1">
              <i class="bi bi-arrow-right-short"></i>
              Sending
              <span class="fw-semibold">{{ conversionLabel }}</span>
            </div>
          } @else if (canUseBoxes && isBoxMode) {
            <div class="text-muted small mt-1">1 box = {{ context.nos }} units</div>
          }

          @if (quantityControl.touched && quantityControl.errors?.['required']) {
            <div class="text-danger small mt-1">Enter a quantity.</div>
          } @else if (quantityControl.touched && quantityControl.errors?.['quantityNotPositive']) {
            <div class="text-danger small mt-1">
              Enter a number above zero. Use the Add / Remove toggle to set the direction.
            </div>
          } @else if (quantityControl.touched && quantityControl.errors?.['quantityInvalid']) {
            <div class="text-danger small mt-1">Enter a valid number.</div>
          } @else if (quantityError) {
            <div class="text-danger small mt-1">{{ quantityError }}</div>
          }

          <!-- The shortage response, rendered against the field that caused it. -->
          @if (shortageError) {
            <div class="alert alert-danger py-2 px-3 small mt-2 mb-0">
              <div class="fw-semibold mb-1">{{ shortageError.quantity }}</div>
              <div class="d-flex flex-wrap gap-3">
                <span>
                  Available:
                  <strong>{{ formatQuantity(shortageError.available) }} units</strong>
                </span>
                <span>
                  Requested:
                  <strong>{{ formatQuantity(shortageError.requested) }} units</strong>
                </span>
                <span>
                  Short by:
                  <strong>{{ formatQuantity(shortageError.shortage) }} units</strong>
                </span>
              </div>
            </div>
          }
        </div>

        <div class="col-md-6">
          <label class="form-label">
            Reason
            <span class="text-danger">*</span>
          </label>
          <ng-select
            placeholder="Select a reason"
            [items]="reasonOptions"
            formControlName="reason"
            bindValue="value"
            bindLabel="label"
            [clearable]="false"
            [searchable]="false" />
          @if (reasonControl.touched && reasonControl.invalid) {
            <div class="text-danger small mt-1">Pick the reason for this adjustment.</div>
          }
        </div>

        <div class="col-12">
          <label for="adjustmentRemarks" class="form-label">
            Remarks
            @if (isRemarksRequired) {
              <span class="text-danger">*</span>
            } @else {
              <span class="text-muted small fw-normal">(optional)</span>
            }
          </label>
          <textarea
            id="adjustmentRemarks"
            class="form-control shadow-none"
            formControlName="remarks"
            rows="2"
            placeholder="What happened, in one line"
            [class.is-invalid]="(remarksControl.invalid && remarksControl.touched) || !!remarksError"
            (input)="remarksError = null"></textarea>
          @if (remarksControl.touched && remarksControl.errors?.['required']) {
            <div class="text-danger small mt-1">Remarks are required when the reason is "Other".</div>
          } @else if (remarksError) {
            <div class="text-danger small mt-1">{{ remarksError }}</div>
          }
        </div>
      </div>

      <!-- Both balances this adjustment moves, recomputed as they type. -->
      @if (batchBalanceLabel) {
        <div class="balance-strip mt-3">
          <div class="row g-2 small">
            <div class="col-md-6">
              <span class="text-muted">Batch {{ context.batch_number }}:</span>
              <span class="fw-semibold ms-1">{{ batchBalanceLabel }}</span>
            </div>
            @if (containerBalanceBefore !== null) {
              <div class="col-md-6">
                <span class="text-muted">{{ context.product_name }} at {{ containerName }}:</span>
                <span class="fw-semibold ms-1">
                  {{ formatQuantity(context.container_units_available) }} units now
                  @if (containerBalanceAfter !== null) {
                    , {{ formatQuantity(containerBalanceAfter.toString()) }} after
                  }
                </span>
              </div>
            }
          </div>
        </div>
      }

      @if (submitError) {
        <app-error-alert class="d-block mt-3" [error]="submitError"></app-error-alert>
      }
    </div>

    <div class="modal-footer p-4 pt-0 border-0">
      <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="isSubmitting">Cancel</button>
      <button
        type="submit"
        class="btn"
        [class.btn-danger]="isRemoving"
        [class.btn-primary]="!isRemoving"
        [disabled]="isSubmitting">
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm me-1"></span>
          Saving...
        } @else {
          <i class="bi bi-check-circle me-1"></i>
          {{ isRemoving ? 'Remove stock' : 'Add stock' }}
        }
      </button>
    </div>
  </form>
}
